/**
 * The pure part of the damage smoke: a small recycled pool of puffs that spawn
 * behind a hurt aeroplane, drift and fade. `sprite-effects.ts` draws each one
 * as the original's own `SMOKE.BMP` puff — the `PointsMaterial` dot this
 * module was written for is gone.
 *
 * Kept free of three.js so the lifecycle is unit-testable. Cheap on purpose: a
 * fixed pool, no allocation per puff, and nothing at all while the aircraft is
 * healthy.
 *
 * ## What the original's own data settles
 *
 * - **A puff does not animate.** `SMOKE.TBL` is a single object with **one**
 *   state and **twelve LODs** (docs/formats/models-tbl.md §4.3): the twelve
 *   frames are size steps of one puff, picked by apparent size, not an
 *   animation. So a puff's drawn size is a function of *distance* and never of
 *   age — which is what `sprite-effects.ts` does, and why nothing here grows a
 *   puff. Opacity is the only thing that changes with age, and that is a port
 *   addition (the sheet has no alpha ramp).
 * - **It is dark grey, not white.** `SMOKE.BMP`'s twelve frames use exactly
 *   eleven palette entries, `BARON.PAL` 64–74, a neutral ramp from `#141414`
 *   to `#696969`. Anything that tints it lighter is wrong.
 * - **Nothing on the ground emits it.** Two searches, both over the whole
 *   corpus: no `TERR1-5`/`MISC` object outside `MISC#14` carries a sprite node
 *   at all (only `FLAK`, `EXPLO`, `SMOKE` and `MISC` do), and **no `.WLD`
 *   placement in any of the six regions references `SMOKE`**. So factory
 *   chimneys and towns do not smoke in the original, and this port should not
 *   invent it.
 *
 * ## SETTLED, 2026-08-20 — by reading `s\combat\smoke.c`, not by watching
 *
 * The 2026-08-19 capture session (`re-sessions/smoke`) could not pin the
 * cadence, and produced one firm negative: a D.H.2 flown for two minutes under
 * continuous fire — the "You've been hit!" banner up in every frame — trailed
 * **no smoke at all** across ~70 frames. The flak lane settled the question the
 * other way round, by disassembling the module (`re-sessions/flak`, AD-010 §7).
 *
 * - **`SMOKE.DAT` is 16 bytes and `smoke.c` reads all of them in one `fread`**,
 *   so the whole file is accounted for: `500, 3000, 10, 50, 500, 100, 128, 4`.
 *   The first is the **spawn interval**, the second the **puff lifetime** — the
 *   allocator proves it, sizing the trail buffer at `3000/500 + 2 = 8` puffs.
 *   The last, 4, is the cap on simultaneous trails.
 * - **One timer unit is 0.840 ms**, measured: `smoke.c` and `aa.c` share one
 *   countdown helper draining one global frame delta, so the flak lane's
 *   measurement of that drain against the guest's BIOS tick (AD-010 §4) is the
 *   unit here too. So the interval is **0.42 s** and a puff lives **2.52 s**,
 *   leaving **six** puffs in a steady-state trail.
 * - **The trigger is a cumulative damage count, not a health fraction.**
 *   `damage.c` calls `smoke_start` from exactly two places. One is the
 *   set-on-fire path. The other is the ordinary damage path, guarded by
 *   `if (aircraft->totalDamage - 25 < 0) return;` — an aeroplane starts smoking
 *   when the *sum of everything that has hit it* passes 25, and the severity
 *   ladder above that runs in bands of 16 to a maximum of 7, i.e. saturating at
 *   137. That is why the D.H.2 never smoked: the old `engineHealth < 0.7` trip
 *   fires at 30 % of *one* component, which is a far lower bar than 25/137 ≈
 *   18 % of the whole airframe's tabulated capacity.
 *
 * The port keeps its own five-component damage model rather than adding the
 * original's single accumulator, so the trigger is ported as the same
 * *fraction*: total damage across the five components, over their combined
 * capacity, past 18.2 %. `smokeIntensity` still scales with how far past the
 * trip an aeroplane is, which the original does through the severity bands.
 */

export interface Puff {
  x: number;
  y: number;
  z: number;
  /** Seconds lived; `>= lifeSeconds` means the slot is free. */
  age: number;
  /**
   * 0 = engine damage smoke, 1 = fire. Recorded per puff because the source's
   * state at emission is what a trail remembers; the sprite renderer draws one
   * sheet either way, so nothing reads it today.
   */
  heat: number;
}

export interface SmokeSource {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /**
   * 0..1 — how far the whole airframe is from pristine, the port's stand-in for
   * the original's single cumulative damage count. Past
   * `SMOKE_DAMAGE_THRESHOLD` it starts trailing.
   */
  readonly damageFraction: number;
  readonly burning: boolean;
}

/**
 * The register, kept as a register even now that three of its four entries are
 * RECOVERED — the fourth is still a guess, and the point of the shape is that a
 * guess is an enumerable work item rather than a comment.
 */
export const SMOKE_TUNABLES = {
  /** Whole-airframe damage past which an aeroplane trails smoke. RECOVERED: `damage.c` trips at 25 on a ladder saturating at 137. */
  smokeDamageThreshold: 25 / 137,
  /** Seconds a puff stays on screen. RECOVERED: `SMOKE.DAT[1]` = 3000 timer units. */
  puffLifeSeconds: 3000 * 0.000_84,
  /** Puffs per second. RECOVERED: `SMOKE.DAT[0]` = 500 timer units between puffs. */
  puffRatePerSecond: 1 / (500 * 0.000_84),
  /** How fast a puff rises as it dissipates, m/s. Still CALIBRATION-PENDING — nothing in the data is a rise rate. */
  puffRiseMps: 3,
} as const;

export type SmokeTunableKey = keyof typeof SMOKE_TUNABLES;

/** Read out of `SMOKE.DAT` and `RB.EXE` (AD-010 §7), so no longer guesses. */
export const SMOKE_RECOVERED_TUNABLES: readonly SmokeTunableKey[] = [
  "smokeDamageThreshold",
  "puffLifeSeconds",
  "puffRatePerSecond",
];

/** Whatever is left. Derived, so a new key cannot escape the queue by being forgotten. */
export const SMOKE_CALIBRATION_PENDING: readonly SmokeTunableKey[] = (
  Object.keys(SMOKE_TUNABLES) as SmokeTunableKey[]
).filter((key) => !SMOKE_RECOVERED_TUNABLES.includes(key));

/** Whole-airframe damage past which an aeroplane trails smoke. */
export const SMOKE_DAMAGE_THRESHOLD = SMOKE_TUNABLES.smokeDamageThreshold;
export const PUFF_LIFE_SECONDS = SMOKE_TUNABLES.puffLifeSeconds;
/**
 * `smoke.c` allocates `SMOKE.DAT[7]` = 4 trails of `3000/500 + 2` = 8 puffs, so
 * 32 is the original's own ceiling rather than a rendering budget.
 */
export const MAX_PUFFS = 32;
/** Puffs per second at full rate. */
export const PUFF_RATE_PER_SECOND = SMOKE_TUNABLES.puffRatePerSecond;
/** How fast a puff rises as it dissipates, m/s. */
export const PUFF_RISE_MPS = SMOKE_TUNABLES.puffRiseMps;

export function emptyPuffs(count: number = MAX_PUFFS): Puff[] {
  return Array.from({ length: count }, () => ({ x: 0, y: 0, z: 0, age: PUFF_LIFE_SECONDS, heat: 0 }));
}

/**
 * 0 when healthy and not burning, rising to 1 for a well-alight aeroplane.
 * Between the trip and total wreckage it ramps, which is the port's continuous
 * reading of the original's eight severity bands.
 */
export function smokeIntensity(source: SmokeSource): number {
  if (source.burning) return 1;
  if (source.damageFraction <= SMOKE_DAMAGE_THRESHOLD) return 0;
  const past = (source.damageFraction - SMOKE_DAMAGE_THRESHOLD) / (1 - SMOKE_DAMAGE_THRESHOLD);
  return Math.min(1, past) * 0.7;
}

/**
 * The port's stand-in for the original's single cumulative damage count: how
 * far the five airframe components are, on average, from pristine. The original
 * keeps one running total per aeroplane; this port keeps five healths, and the
 * mean is the only reading of them that makes "the whole airframe is 18 % gone"
 * mean the same thing in both.
 */
export function airframeDamageFraction(components: {
  readonly engine: number;
  readonly wingLeft: number;
  readonly wingRight: number;
  readonly controls: number;
  readonly fuel: number;
}): number {
  const total =
    components.engine + components.wingLeft + components.wingRight + components.controls + components.fuel;
  return Math.min(1, Math.max(0, 1 - total / 5));
}

/** 0 (invisible) to 1 (freshly emitted). */
export function puffOpacity(puff: Puff): number {
  if (puff.age >= PUFF_LIFE_SECONDS) return 0;
  return 1 - puff.age / PUFF_LIFE_SECONDS;
}

export interface SmokeState {
  readonly puffs: Puff[];
  /** Fractional puffs owed, carried between frames so the rate is frame-independent. */
  emission: number;
}

export function createSmokeState(): SmokeState {
  return { puffs: emptyPuffs(), emission: 0 };
}

function freeSlot(puffs: readonly Puff[]): Puff | undefined {
  let oldest: Puff | undefined;
  for (const puff of puffs) {
    if (puff.age >= PUFF_LIFE_SECONDS) return puff;
    if (!oldest || puff.age > oldest.age) oldest = puff;
  }
  return oldest;
}

/**
 * Ages every puff and emits new ones behind `sources`. Mutates the pool in
 * place — this runs every frame, and a fresh array per frame is exactly the
 * kind of garbage a 60 Hz loop should not make.
 */
export function advanceSmoke(state: SmokeState, sources: readonly SmokeSource[], dt: number): SmokeState {
  for (const puff of state.puffs) {
    if (puff.age >= PUFF_LIFE_SECONDS) continue;
    puff.age += dt;
    puff.y += PUFF_RISE_MPS * dt;
  }

  let owed = state.emission;
  for (const source of sources) {
    const intensity = smokeIntensity(source);
    if (intensity <= 0) continue;
    owed += intensity * PUFF_RATE_PER_SECOND * dt;
    while (owed >= 1) {
      owed -= 1;
      const slot = freeSlot(state.puffs);
      if (!slot) break;
      slot.x = source.x;
      slot.y = source.y;
      slot.z = source.z;
      slot.age = 0;
      slot.heat = source.burning ? 1 : 0;
    }
  }
  state.emission = owed % 1;
  return state;
}

/** How many puffs are currently visible — what the renderer sets its draw range to. */
export function livePuffCount(state: SmokeState): number {
  return state.puffs.filter((puff) => puff.age < PUFF_LIFE_SECONDS).length;
}
